// World Chunking with Streaming
// Inspired by WoW's seamless world loading
// Divide world into chunks, load adjacent chunks asynchronously

import * as THREE from 'three';

export interface ChunkCoord {
  x: number;
  z: number;
}

export interface ChunkData {
  coord: ChunkCoord;
  entities: ChunkEntity[];
  terrain?: THREE.Object3D;
  decorations?: THREE.Object3D[];
  loaded: boolean;
  lastAccessTime: number;
}

export interface ChunkEntity {
  id: string;
  type: 'enemy' | 'npc' | 'loot' | 'object';
  position: { x: number; y: number; z: number };
  data: Record<string, unknown>;
}

export interface WorldConfig {
  chunkSize: number;        // Size of each chunk in world units
  loadDistance: number;     // Number of chunks to load around player
  unloadDistance: number;   // Number of chunks before unloading
  maxLoadedChunks: number;  // Maximum chunks in memory
}

const DEFAULT_CONFIG: WorldConfig = {
  chunkSize: 64,
  loadDistance: 2,
  unloadDistance: 4,
  maxLoadedChunks: 25,
};

type ChunkLoadCallback = (chunk: ChunkData) => Promise<void>;
type ChunkUnloadCallback = (chunk: ChunkData) => void;

export class WorldChunkManager {
  private scene: THREE.Scene;
  private config: WorldConfig;

  // Chunk storage
  private chunks: Map<string, ChunkData> = new Map();
  private loadingChunks: Set<string> = new Set();

  // Current player position
  private currentChunk: ChunkCoord = { x: 0, z: 0 };
  private lastUpdateChunk: ChunkCoord = { x: 0, z: 0 };

  // Callbacks
  private onChunkLoad?: ChunkLoadCallback;
  private onChunkUnload?: ChunkUnloadCallback;

  // Background loading queue
  private loadQueue: ChunkCoord[] = [];
  private isProcessingQueue = false;

  // Performance tracking
  private loadTimes: number[] = [];
  private readonly MAX_LOAD_TIME_SAMPLES = 20;

  constructor(
    scene: THREE.Scene,
    config?: Partial<WorldConfig>,
    onChunkLoad?: ChunkLoadCallback,
    onChunkUnload?: ChunkUnloadCallback
  ) {
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.onChunkLoad = onChunkLoad;
    this.onChunkUnload = onChunkUnload;
  }

  public getChunkKey(coord: ChunkCoord): string {
    return `${coord.x},${coord.z}`;
  }

  public worldToChunk(worldX: number, worldZ: number): ChunkCoord {
    return {
      x: Math.floor(worldX / this.config.chunkSize),
      z: Math.floor(worldZ / this.config.chunkSize),
    };
  }

  public chunkToWorld(coord: ChunkCoord): { x: number; z: number } {
    return {
      x: coord.x * this.config.chunkSize + this.config.chunkSize / 2,
      z: coord.z * this.config.chunkSize + this.config.chunkSize / 2,
    };
  }

  public updatePlayerPosition(worldX: number, worldZ: number): void {
    this.currentChunk = this.worldToChunk(worldX, worldZ);

    // Only update chunks if player moved to a new chunk
    if (
      this.currentChunk.x !== this.lastUpdateChunk.x ||
      this.currentChunk.z !== this.lastUpdateChunk.z
    ) {
      this.lastUpdateChunk = { ...this.currentChunk };
      this.updateLoadedChunks();
    }
  }

  private updateLoadedChunks(): void {
    const { loadDistance, unloadDistance } = this.config;
    const chunksToLoad: ChunkCoord[] = [];
    const chunksToUnload: string[] = [];

    // Determine chunks that should be loaded (spiral pattern for priority)
    for (let dx = -loadDistance; dx <= loadDistance; dx++) {
      for (let dz = -loadDistance; dz <= loadDistance; dz++) {
        const coord: ChunkCoord = {
          x: this.currentChunk.x + dx,
          z: this.currentChunk.z + dz,
        };
        const key = this.getChunkKey(coord);

        if (!this.chunks.has(key) && !this.loadingChunks.has(key)) {
          chunksToLoad.push(coord);
        }
      }
    }

    // Sort by distance from player (closest first)
    chunksToLoad.sort((a, b) => {
      const distA = Math.abs(a.x - this.currentChunk.x) + Math.abs(a.z - this.currentChunk.z);
      const distB = Math.abs(b.x - this.currentChunk.x) + Math.abs(b.z - this.currentChunk.z);
      return distA - distB;
    });

    // Determine chunks that should be unloaded
    for (const [key, chunk] of this.chunks) {
      const dx = Math.abs(chunk.coord.x - this.currentChunk.x);
      const dz = Math.abs(chunk.coord.z - this.currentChunk.z);

      if (dx > unloadDistance || dz > unloadDistance) {
        chunksToUnload.push(key);
      }
    }

    // Unload distant chunks
    for (const key of chunksToUnload) {
      this.unloadChunk(key);
    }

    // Enforce max loaded chunks by removing oldest accessed
    while (this.chunks.size >= this.config.maxLoadedChunks && chunksToUnload.length === 0) {
      let oldestKey = '';
      let oldestTime = Infinity;

      for (const [key, chunk] of this.chunks) {
        if (chunk.lastAccessTime < oldestTime) {
          // Don't unload chunks near player
          const dx = Math.abs(chunk.coord.x - this.currentChunk.x);
          const dz = Math.abs(chunk.coord.z - this.currentChunk.z);
          if (dx > 1 || dz > 1) {
            oldestTime = chunk.lastAccessTime;
            oldestKey = key;
          }
        }
      }

      if (oldestKey) {
        this.unloadChunk(oldestKey);
      } else {
        break;
      }
    }

    // Queue chunks for loading
    for (const coord of chunksToLoad) {
      this.queueChunkLoad(coord);
    }

    // Process load queue
    this.processLoadQueue();
  }

  private queueChunkLoad(coord: ChunkCoord): void {
    const key = this.getChunkKey(coord);
    if (this.loadingChunks.has(key)) return;

    this.loadQueue.push(coord);
    this.loadingChunks.add(key);
  }

  private async processLoadQueue(): Promise<void> {
    if (this.isProcessingQueue || this.loadQueue.length === 0) return;

    this.isProcessingQueue = true;

    // Load chunks one at a time to avoid frame drops
    while (this.loadQueue.length > 0) {
      const coord = this.loadQueue.shift()!;
      await this.loadChunk(coord);

      // Yield to main thread
      await new Promise(resolve => requestAnimationFrame(resolve));
    }

    this.isProcessingQueue = false;
  }

  private async loadChunk(coord: ChunkCoord): Promise<void> {
    const key = this.getChunkKey(coord);
    const startTime = performance.now();

    // Create chunk data
    const chunk: ChunkData = {
      coord,
      entities: [],
      loaded: false,
      lastAccessTime: Date.now(),
    };

    // Generate or load terrain for this chunk
    chunk.terrain = this.generateChunkTerrain(coord);
    if (chunk.terrain) {
      this.scene.add(chunk.terrain);
    }

    // Load chunk-specific entities
    chunk.entities = await this.loadChunkEntities(coord);

    // Call custom load handler
    if (this.onChunkLoad) {
      await this.onChunkLoad(chunk);
    }

    chunk.loaded = true;
    this.chunks.set(key, chunk);
    this.loadingChunks.delete(key);

    // Track load time
    const loadTime = performance.now() - startTime;
    this.loadTimes.push(loadTime);
    if (this.loadTimes.length > this.MAX_LOAD_TIME_SAMPLES) {
      this.loadTimes.shift();
    }
  }

  private generateChunkTerrain(coord: ChunkCoord): THREE.Object3D | undefined {
    // Generate a simple ground plane for each chunk
    const worldPos = this.chunkToWorld(coord);
    const size = this.config.chunkSize;

    const geometry = new THREE.PlaneGeometry(size, size);
    geometry.rotateX(-Math.PI / 2);

    // Vary ground color slightly per chunk for visibility during development
    const colorVariation = ((coord.x * 73856093) ^ (coord.z * 19349663)) % 30 - 15;
    const baseColor = 0x228b22; // Forest green
    const r = ((baseColor >> 16) & 0xff) + colorVariation;
    const g = ((baseColor >> 8) & 0xff) + colorVariation;
    const b = (baseColor & 0xff) + colorVariation;
    const color = (r << 16) | (g << 8) | b;

    const material = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.8,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(worldPos.x, 0, worldPos.z);
    mesh.receiveShadow = true;
    mesh.name = `chunk_terrain_${coord.x}_${coord.z}`;

    return mesh;
  }

  private async loadChunkEntities(_coord: ChunkCoord): Promise<ChunkEntity[]> {
    // Override this method or use callback to load actual entities
    // For now, return empty array - entities are populated via callback
    return [];
  }

  private unloadChunk(key: string): void {
    const chunk = this.chunks.get(key);
    if (!chunk) return;

    // Remove terrain from scene
    if (chunk.terrain) {
      this.scene.remove(chunk.terrain);
      if (chunk.terrain instanceof THREE.Mesh) {
        chunk.terrain.geometry.dispose();
        (chunk.terrain.material as THREE.Material).dispose();
      }
    }

    // Remove decorations
    if (chunk.decorations) {
      for (const decoration of chunk.decorations) {
        this.scene.remove(decoration);
      }
    }

    // Call custom unload handler
    if (this.onChunkUnload) {
      this.onChunkUnload(chunk);
    }

    this.chunks.delete(key);
  }

  // Get chunk at world position
  public getChunkAt(worldX: number, worldZ: number): ChunkData | undefined {
    const coord = this.worldToChunk(worldX, worldZ);
    const key = this.getChunkKey(coord);
    const chunk = this.chunks.get(key);

    if (chunk) {
      chunk.lastAccessTime = Date.now();
    }

    return chunk;
  }

  // Get all loaded chunks
  public getLoadedChunks(): ChunkData[] {
    return Array.from(this.chunks.values());
  }

  // Get entities in a radius
  public getEntitiesInRadius(
    worldX: number,
    worldZ: number,
    radius: number
  ): ChunkEntity[] {
    const results: ChunkEntity[] = [];
    const radiusSq = radius * radius;

    // Calculate chunk range to check
    const minChunk = this.worldToChunk(worldX - radius, worldZ - radius);
    const maxChunk = this.worldToChunk(worldX + radius, worldZ + radius);

    for (let cx = minChunk.x; cx <= maxChunk.x; cx++) {
      for (let cz = minChunk.z; cz <= maxChunk.z; cz++) {
        const key = this.getChunkKey({ x: cx, z: cz });
        const chunk = this.chunks.get(key);

        if (chunk) {
          for (const entity of chunk.entities) {
            const dx = entity.position.x - worldX;
            const dz = entity.position.z - worldZ;
            if (dx * dx + dz * dz <= radiusSq) {
              results.push(entity);
            }
          }
        }
      }
    }

    return results;
  }

  // Add entity to appropriate chunk
  public addEntity(entity: ChunkEntity): void {
    const coord = this.worldToChunk(entity.position.x, entity.position.z);
    const key = this.getChunkKey(coord);
    const chunk = this.chunks.get(key);

    if (chunk) {
      chunk.entities.push(entity);
    }
  }

  // Remove entity from chunks
  public removeEntity(entityId: string): void {
    for (const chunk of this.chunks.values()) {
      const index = chunk.entities.findIndex(e => e.id === entityId);
      if (index !== -1) {
        chunk.entities.splice(index, 1);
        return;
      }
    }
  }

  // Move entity between chunks if needed
  public updateEntityPosition(
    entityId: string,
    newX: number,
    newZ: number
  ): void {
    // Find current chunk
    let currentChunk: ChunkData | undefined;
    let entityIndex = -1;

    for (const chunk of this.chunks.values()) {
      const index = chunk.entities.findIndex(e => e.id === entityId);
      if (index !== -1) {
        currentChunk = chunk;
        entityIndex = index;
        break;
      }
    }

    if (!currentChunk || entityIndex === -1) return;

    const entity = currentChunk.entities[entityIndex];
    const newCoord = this.worldToChunk(newX, newZ);

    // Check if entity moved to a different chunk
    if (
      newCoord.x !== currentChunk.coord.x ||
      newCoord.z !== currentChunk.coord.z
    ) {
      // Remove from old chunk
      currentChunk.entities.splice(entityIndex, 1);

      // Update position
      entity.position.x = newX;
      entity.position.z = newZ;

      // Add to new chunk
      const newKey = this.getChunkKey(newCoord);
      const newChunk = this.chunks.get(newKey);

      if (newChunk) {
        newChunk.entities.push(entity);
      }
    } else {
      // Just update position within same chunk
      entity.position.x = newX;
      entity.position.z = newZ;
    }
  }

  // Performance stats
  public getStats(): {
    loadedChunks: number;
    loadingChunks: number;
    queuedChunks: number;
    averageLoadTime: number;
  } {
    return {
      loadedChunks: this.chunks.size,
      loadingChunks: this.loadingChunks.size,
      queuedChunks: this.loadQueue.length,
      averageLoadTime:
        this.loadTimes.length > 0
          ? this.loadTimes.reduce((a, b) => a + b, 0) / this.loadTimes.length
          : 0,
    };
  }

  // Force load a specific chunk (for teleportation, etc.)
  public async forceLoadChunk(coord: ChunkCoord): Promise<ChunkData | null> {
    const key = this.getChunkKey(coord);

    if (this.chunks.has(key)) {
      return this.chunks.get(key)!;
    }

    await this.loadChunk(coord);
    return this.chunks.get(key) ?? null;
  }

  // Cleanup
  public destroy(): void {
    for (const key of Array.from(this.chunks.keys())) {
      this.unloadChunk(key);
    }
    this.loadQueue.length = 0;
    this.loadingChunks.clear();
  }
}

// Web Worker for background chunk processing
export const ChunkWorkerCode = `
  self.onmessage = function(e) {
    const { type, data } = e.data;

    switch(type) {
      case 'generateTerrain':
        // Generate heightmap or terrain data
        const { coord, size, seed } = data;
        const heightmap = generateHeightmap(coord.x, coord.z, size, seed);
        self.postMessage({ type: 'terrainData', coord, heightmap });
        break;

      case 'processEntities':
        // Process/filter entities for a chunk
        const { entities, playerPos, radius } = data;
        const filtered = filterEntities(entities, playerPos, radius);
        self.postMessage({ type: 'filteredEntities', entities: filtered });
        break;
    }
  };

  function generateHeightmap(cx, cz, size, seed) {
    const data = new Float32Array(size * size);
    for (let z = 0; z < size; z++) {
      for (let x = 0; x < size; x++) {
        const wx = cx * size + x;
        const wz = cz * size + z;
        // Simple noise-like function
        data[z * size + x] = Math.sin(wx * 0.1 + seed) * Math.cos(wz * 0.1) * 2;
      }
    }
    return data;
  }

  function filterEntities(entities, playerPos, radius) {
    const radiusSq = radius * radius;
    return entities.filter(e => {
      const dx = e.position.x - playerPos.x;
      const dz = e.position.z - playerPos.z;
      return dx * dx + dz * dz <= radiusSq;
    });
  }
`;
